import React from "react";
import { Autoplay, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { useTranslation } from 'react-i18next';

// Images
import LandingImg from "../../src/assets/images/adobe/landing-img.webp";
import OnsiteImage from "../../src/assets/images/adobe/Speaker.webp";
import OPIImage from "../../src/assets/images/adobe/adobe2.webp";
import VriImg from "../../src/assets/images/adobe/vri-img.webp";
import ASLImage from "../../src/assets/images/adobe/ASL.webp";
import Consecutive from "../../src/assets/images/adobe/adobe3.webp";
import Simultaneous from "../../src/assets/images/adobe/SanDiego1.webp";
import LegalImage from "../../src/assets/images/adobe/Legal1.webp";
import Medical from "../../src/assets/images/adobe/medical1.webp";
import Immigration from "../../src/assets/images/adobe/adobe4.webp";
import Corporate from "../../src/assets/images/adobe/adobe5.webp";
import Education from "../../src/assets/images/adobe/NYC1.webp";
import Government from "../../src/assets/images/adobe/adobe7.webp";

const LandingInfo: React.FC = () => {
  const { t, ready } = useTranslation();
  if (!ready) return null;

  const interpretationTypes = [
    {
      key: 'onSite',
      image: OnsiteImage,
      link: '/interpretation-types/on-site',
    },
    {
      key: 'overThePhone',
      image: OPIImage,
      link: '/interpretation-types/phone-interpretation',
    },
    {
      key: 'videoRemote',
      image: VriImg,
      link: '/interpretation-types/vri',
    },
    {
      key: 'signLanguage',
      image: ASLImage,
      link: '/interpretation-types/asl-interpretation',
    },
    {
      key: 'consecutive',
      image: Consecutive,
      link: '/interpretation-types/on-site/consecutive',
    },
    {
      key: 'simultaneous',
      image: Simultaneous,
      link: '/interpretation-types/on-site/simultaneous',
    },
  ];

  const industries = [
    {
      key: 'legal',
      image: LegalImage,
      link: '/expertise/legal-interpretation',
    },
    {
      key: 'healthcare',
      image: Medical,
      link: '/expertise/medical-interpretation',
    },
    {
      key: 'immigration',
      image: Immigration,
      link: '/expertise/immigration-interpretation',
    },
    {
      key: 'corporate',
      image: Corporate,
      link: '/expertise/business-interpretation',
    },
    {
      key: 'education',
      image: Education,
      link: '/expertise/educational-interpretation',
    },
    {
      key: 'government',
      image: Government,
      link: '/expertise/government-interpretation',
    },
  ];

  const languagesList = t('landingInfo.languages', { returnObjects: true });

  return (
    <div className="container mx-auto px-4 py-8 space-y-6">
      {/* Title Section */}
      <div className="text-center ">
        <h2 className="text-4xl font-bold text-gray-800">
          {t('landingInfo.title')}
        </h2>
        <p className="text-lg text-gray-600">
          {t('landingInfo.subtitle')}
        </p>
      </div>

      {/* Presentation Section */}
      <div className="flex flex-col lg:flex-row items-center text-center gap-8 bg-blue-50 rounded-lg shadow">
        <img
          src={LandingImg}
          alt="Trusted Experts in Interpretation Services"
          className="w-full lg:w-1/3 rounded-lg shadow-md"
        />
        <div className="lg:w-2/3">
          <h3 className="text-2xl font-bold text-gray-800 mb-4">
            {t('landingInfo.presentationTitle')}
          </h3>
          <p className="text-lg text-gray-600 mb-6">
            {t('landingInfo.presentationText')}
          </p>
        </div>
      </div>

      {/* Card Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Interpretation Types */}
        <div className="bg-gray-100 rounded-lg shadow-lg relative">
          <h3 className="text-xl font-semibold mb-2 text-center text-gray-800 pt-2">
            {t('landingInfo.categories.interpretationTypes')}
          </h3>
          <div className="border-t border-gray-300 w-full"></div>
          <div className="relative">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              navigation={{
                nextEl: ".swiper-button-next-interpretation",
                prevEl: ".swiper-button-prev-interpretation",
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000 }}
              spaceBetween={1}
              slidesPerView={1}
              className="w-full"
            >
              {interpretationTypes.map((item, index) => (
                <SwiperSlide key={index}>
                  <div className="flex items-center min-h-44">
                    <img
                      src={item.image}
                      alt={t(`landingInfo.cards.${item.key}.name`)}
                      className="w-2/5 h-full object-cover md:rounded-bl-lg"
                    />
                    <div className="flex-1 p-4">
                      <a
                        href={item.link}
                        className="text-lg font-medium text-gray-800"
                      >
                        {t(`landingInfo.cards.${item.key}.name`)}
                      </a>
                      <p className="text-sm text-gray-600">
                        {t(`landingInfo.cards.${item.key}.description`)}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {/* Navigation buttons */}
            <div className="swiper-button-prev-interpretation absolute left-[-25px] top-1/2 transform -translate-y-1/2 text-3xl text-gray-800 p-2 cursor-pointer z-10">
              &#x276E;
            </div>
            <div className="swiper-button-next-interpretation absolute right-[-25px] top-1/2 transform -translate-y-1/2 text-3xl text-gray-800 p-2 cursor-pointer z-10">
              &#x276F;
            </div>
          </div>
        </div>

        {/* Industries Expertise */}
        <div className="bg-gray-100 rounded-lg shadow-lg relative">
          <h3 className="text-xl font-semibold mb-2 text-center text-gray-800 pt-2">
            {t('landingInfo.categories.industriesExpertise')}
          </h3>
          <div className="border-t border-gray-300 w-full"></div>
          <div className="relative">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              navigation={{
                nextEl: ".swiper-button-next-industries",
                prevEl: ".swiper-button-prev-industries",
              }}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000 }}
              spaceBetween={1}
              slidesPerView={1}
              className="w-full"
            >
              {industries.map((item, index) => (
                <SwiperSlide key={index}>
                  <div className="flex items-center min-h-44">
                    <img
                      src={item.image}
                      alt={t(`landingInfo.cards.${item.key}.name`)}
                      className="w-2/5 h-full object-cover md:rounded-bl-lg"
                    />
                    <div className="flex-1 p-4">
                      <a
                        href={item.link}
                        className="text-lg font-medium text-gray-800"
                      >
                        {t(`landingInfo.cards.${item.key}.name`)}
                      </a>
                      <p className="text-sm text-gray-600">
                        {t(`landingInfo.cards.${item.key}.description`)}
                      </p>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {/* Navigation buttons */}
            <div className="swiper-button-prev-industries absolute left-[-25px] top-1/2 transform -translate-y-1/2 text-3xl text-gray-800 p-2 cursor-pointer z-10">
              &#x276E;
            </div>
            <div className="swiper-button-next-industries absolute right-[-25px] top-1/2 transform -translate-y-1/2 text-3xl text-gray-800 p-2 cursor-pointer z-10">
              &#x276F;
            </div>
          </div>
        </div>
      </div>

      {/* Languages Section */}
      <div className="invisible md:visible border-t border-gray-300 w-full"></div>

      <div className="flex flex-col items-center space-y-4">
        <h2 className="text-2xl font-semibold text-gray-800 mt-[-15px]">
          {t('faq.languagesOfExpertise')}
        </h2>
        <ul className="grid grid-cols-2 gap-4 text-gray-700 mt-4 md:flex md:flex-wrap md:justify-center md:gap-8">
          {Array.isArray(languagesList) && languagesList.map((language, index) => (
            <li key={index} className="flex items-center space-x-2 text-lg">
              {/* Checkmark Icon */}
              <svg
                className="w-5 h-5 text-green-500"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
              {/* Language Name */}
              <span className="hover:text-blue-600 transition-colors duration-200">
                {language}
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

export default LandingInfo;
